import { useMutation, useQueryClient } from "@tanstack/react-query";
import { EditIcon } from "lucide-react";
import { useState } from "react";
import { Heading } from "react-aria-components";
import z from "zod";

import { Button } from "@/components/base/button";
import { useAppForm } from "@/components/base/form";
import { Modal } from "@/components/base/modal";
import { title } from "@/components/base/primitives";
import { useCanUpdateTournament } from "@/components/tournaments/context";
import { useTournamentDivisionName } from "@/hooks/tournament";
import { useTRPC } from "@/integrations/trpc/react";
import { invalidateMatchQueries } from "@/lib/tournament-cache";

const setMatchCourtSchema = z.object({
  poolId: z.number().optional(),
  playoffMatchId: z.number().optional(),
  court: z.string(),
});

// TODO: confirm works with pools and playoffs

export type SetCourtForm = {
  tournamentId: number;
  tournamentDivisionId: number;
  poolId?: number;
  playoffMatchId?: number;
  name: string;
  court: string | null | undefined;
  completed?: boolean;
};

export function SetCourtForm({
  poolId,
  playoffMatchId,
  name,
  court,
  tournamentId,
  tournamentDivisionId,
  completed,
}: SetCourtForm) {
  const canUpdate = useCanUpdateTournament() && !completed;

  const [isOpen, setOpen] = useState(false);

  const divisionName = useTournamentDivisionName(
    tournamentId,
    tournamentDivisionId,
  );

  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { mutate } = useMutation({
    ...trpc.tournaments.setMatchCourt.mutationOptions(),
    onSuccess: (_) => {
      setOpen(false);

      invalidateMatchQueries(queryClient, trpc);
    },
  });

  const schema = setMatchCourtSchema.pick({
    court: true,
  });

  const form = useAppForm({
    defaultValues: {
      court: court ?? "Court ",
    } as z.infer<typeof setMatchCourtSchema>,
    validators: {
      onMount: schema,
      onChange: schema,
    },
    onSubmit: ({ value: { court } }) => {
      mutate({
        poolId,
        playoffMatchId,
        court,
      });
    },
  });

  return (
    <>
      {court && (
        <span className="flex flex-row items-center gap-2 text-ellipsis">
          {court}
          {canUpdate && (
            <Button
              variant="text"
              className="text-blue-500"
              size="sm"
              onPress={() => setOpen(true)}
              tooltip="Set or update court"
            >
              <EditIcon size={16} />
            </Button>
          )}
        </span>
      )}

      {!court && canUpdate && (
        <Button
          className="text-blue-500"
          size="sm"
          onPress={() => setOpen(true)}
          tooltip="Set or update court"
          variant="link"
        >
          Set Court <EditIcon size={12} />
        </Button>
      )}

      <Modal isOpen={isOpen} onOpenChange={setOpen}>
        <div className="flex flex-col space-y-3 p-3">
          <Heading className={title({ size: "sm" })} slot="title">
            Set Court
          </Heading>
          <p>
            Set <span className="font-semibold italic">{name}</span>
            {"'s "}
            court for the{" "}
            <span className="font-semibold italic">{divisionName}</span>{" "}
            division.
          </p>
          <form
            className="flex flex-col space-y-2"
            onSubmit={(e) => {
              e.preventDefault();

              form.handleSubmit();
            }}
          >
            <form.AppField
              name="court"
              children={(field) => (
                <field.Text isRequired label="Court" field={field} />
              )}
            />

            <form.AppForm>
              <form.Footer className="col-span-full">
                <Button onPress={() => setOpen(false)}>Cancel</Button>

                <form.SubmitButton>Submit</form.SubmitButton>
              </form.Footer>
            </form.AppForm>
          </form>
        </div>
      </Modal>
    </>
  );
}
